import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, X } from "lucide-react";
import { ReactNode } from "react";
import { cn } from "lib/utils";

/**
 * Moldura dos modais no layout novo.
 *
 * Por que nao e o `CustomModal`: aquele desenha uma barra de titulo escura de
 * 60px com o texto em 30px e um circulo de logo flutuando fora do card. O
 * layout novo e o oposto — cabecalho claro, titulo de 17px, icone dentro da
 * caixa — entao mudar o CustomModal no lugar mudaria de uma vez os 16 modais
 * que ainda nao foram redesenhados. Os dois convivem enquanto a migracao
 * acontece modal a modal.
 *
 * Por que o primitivo do Radix e nao `components/ui/dialog`: o DialogContent de
 * la fixa `bg-background` e `border`, que dependem das vars HSL definidas so
 * dentro de `.intrex-admin-scope` (ver src/index.css) — fora dali o fundo sai
 * transparente. Ele tambem nao da acesso ao Overlay para trocar o veu. Do
 * primitivo vem o que interessa de graca: foco preso, Esc, clique fora,
 * `aria-labelledby` e o scroll do body travado.
 */
type Props = {
  show: boolean;
  /** Fecha o modal: Esc, clique no veu e botao X passam por aqui. */
  onClose: () => void;
  titulo: string;
  /** Linha de apoio abaixo do titulo. Some quando nao e passada. */
  subtitulo?: ReactNode;
  /** Icone do cabecalho, no quadrado escuro. Ignorado quando ha `onVoltar`. */
  icone?: ReactNode;
  /** Quando existe, o quadrado do icone vira botao de voltar (telas internas). */
  onVoltar?: () => void;
  /** Botoes a direita do titulo, antes do X. */
  acoes?: ReactNode;
  /** Largura maxima, ex.: "900px". O padrao serve para listagens. */
  largura?: string;
  /**
   * Barra fixa no rodape, fora da area que rola. Vai sem estilo nenhum: cada
   * modal traz o proprio fundo e respiro, porque o rodape ora e uma barra de
   * botoes branca, ora uma faixa de aviso colorida sangrando ate a borda.
   */
  rodape?: ReactNode;
  /** Remove o respiro lateral do corpo, para conteudo que sangra ate a borda. */
  corpoSemPadding?: boolean;
  children: ReactNode;
};

const ModalBase = ({
  show,
  onClose,
  titulo,
  subtitulo,
  icone,
  onVoltar,
  acoes,
  largura = "900px",
  rodape,
  corpoSemPadding,
  children,
}: Props) => {
  return (
    <DialogPrimitive.Root
      open={show}
      onOpenChange={(aberto) => {
        if (!aberto) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="animate-veu-entrada fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[2px]" />

        <DialogPrimitive.Content
          style={{ maxWidth: largura }}
          // A largura e animada porque telas internas do mesmo modal mudam de
          // tamanho (a listagem e mais estreita que o formulario) e o salto
          // seco entre elas chama mais atencao que a propria troca de tela.
          className="animate-modal-entrada fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-white font-Inter text-slate-700 shadow-2xl shadow-slate-900/30 outline-none transition-[max-width] duration-200"
        >
          {/* CABECALHO */}
          <div className="flex shrink-0 items-start gap-3 border-b border-slate-100 px-6 py-4">
            {onVoltar ? (
              <button
                type="button"
                onClick={onVoltar}
                aria-label="Voltar"
                className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            ) : (
              icone && (
                <div className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-sidebar text-white [&>svg]:h-5 [&>svg]:w-5">
                  {icone}
                </div>
              )
            )}

            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="truncate text-[17px] font-semibold leading-tight text-slate-900">
                {titulo}
              </DialogPrimitive.Title>
              {subtitulo && (
                <DialogPrimitive.Description className="mt-1 text-xs leading-none text-slate-400">
                  {subtitulo}
                </DialogPrimitive.Description>
              )}
            </div>

            {acoes && (
              <div className="flex shrink-0 items-center gap-2">{acoes}</div>
            )}

            <DialogPrimitive.Close
              aria-label="Fechar"
              className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          {/* CORPO */}
          <div
            className={cn(
              "flex-1 overflow-y-auto",
              !corpoSemPadding && "px-6 py-5",
            )}
          >
            {children}
          </div>

          {/* RODAPE */}
          {rodape && <div className="shrink-0">{rodape}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default ModalBase;
